import { i18nLike } from "#web/services/i18nLike";
import { getCachedDateOfNow } from "#web/utils/getCachedDateOfNow";
import { useState } from "react";

import { LeakedCode, leakedCodeExists, LeakSource } from "./_models/leakedCode";
import { leakedCodeReader } from "./_runtime";
import { LeakedCodeReader } from "./leakedCodeReader/service";

interface ErrorLeakedCodesExport {
  data: null;
  error: Error;
  loading: false;
}

interface InitialLeakedCodesExport {
  data: null;
  error: null;
  loading: false;
}
type LeakedCodesExportFn = () => void;
type LeakedCodesExportState =
  | ErrorLeakedCodesExport
  | InitialLeakedCodesExport
  | LoadedLeakedCodesExport
  | LoadingLeakedCodesExport;
interface LoadedLeakedCodesExport {
  data: { success: true };
  error: null;
  loading: false;
}
interface LoadingLeakedCodesExport {
  data: null;
  error: null;
  loading: true;
}
interface UseExportLeakedCodesProps {
  currency: string;
}

type UseExportLeakedCodesResult = [LeakedCodesExportState, LeakedCodesExportFn];

const en = {
  common: {
    na: "N/A",
  },
  heading: {
    coupon: "Coupon",
    firstSeen: "First seen",
    lastSeen: "Last seen",
    leakStatus: "Leak status",
    sources: "Sources",
    status: "Status",
    value: "Value",
  },
  status: {
    active: "Active",
    expired: "Expired",
  },
};

const t = i18nLike(en);

const getStatus = (codeEndsAt: Date | null): string => {
  if (codeEndsAt == null || codeEndsAt.getTime() > getCachedDateOfNow().getTime()) {
    return t("status.active");
  }

  return t("status.expired");
};

const formatSources = (leakSources: LeakSource[]): string => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!leakSources || leakSources.length === 0) {
    return "";
  }

  return leakSources.map((source) => source.name).join("; ");
};

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
const formatValue = (currency: string, value: number, valueType: "percentage" | null | string): string => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (value == null) {
    return t("common.na");
  }

  if (valueType === "percentage") {
    return `${String(Math.abs(value))}%`;
  }

  return `${currency} ${Math.abs(value).toFixed(2).replace(".", ",")}`;
};

const toyyyyMMdd = (date: Date | null) => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const escapeCsvValue = (value: null | string) => {
  if (!value) return "";
  let text = value.toString().trim().replace(/\n/g, " ");
  if (text.includes(",")) {
    text = `"${text}"`;
  }
  return text;
};

const processLeakedCode = (leakedCode: LeakedCode, currency: string) => {
  const rowData = [
    leakedCode.code,
    leakedCode.isCurrentlyLeaked ? "Leaked" : "Not leaked",
    getStatus(leakedCode.codeEndsAt),
    formatValue(currency, leakedCode.value, leakedCode.valueType),
    formatSources(leakedCode.sources),
    toyyyyMMdd(leakedCode.leakedAt),
    toyyyyMMdd(leakedCode.lastScrapedAt),
  ].map(escapeCsvValue);

  return rowData.join(",");
};

const generateCsvContent = (leakedCodes: LeakedCode[], currency: string): string => {
  const headings = [
    t("heading.coupon"),
    t("heading.leakStatus"),
    t("heading.status"),
    t("heading.value"),
    t("heading.sources"),
    t("heading.firstSeen"),
    t("heading.lastSeen"),
  ];

  const headerRow = headings.join(",");
  const dataRows = leakedCodes.filter(leakedCodeExists).map((coupon) => processLeakedCode(coupon, currency));

  return [headerRow, ...dataRows].join("\n");
};

const downloadCsv = (csvContent: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  const dateOfNowyyyyMMDD = getCachedDateOfNow().toISOString().split("T")[0]!;
  link.href = url;
  link.setAttribute("download", `leaked_codes_${dateOfNowyyyyMMDD}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportLeakedCodes =
  ({ leakedCodeReader }: { leakedCodeReader: LeakedCodeReader }) =>
  async ({ currency }: { currency: string }) => {
    const { leakedCodes } = await leakedCodeReader.allExisting();
    const csvContent = generateCsvContent(leakedCodes, currency);
    downloadCsv(csvContent);
  };

export const useExportLeakedCodes = ({ currency }: UseExportLeakedCodesProps): UseExportLeakedCodesResult => {
  const [state, setState] = useState<LeakedCodesExportState>({
    data: null,
    error: null,
    loading: false,
  });

  const exportLeakedCodesFn: LeakedCodesExportFn = () => {
    setState({
      data: null,
      error: null,
      loading: true,
    });

    exportLeakedCodes({ leakedCodeReader })({ currency })
      .then(() => {
        setState({
          data: { success: true },
          error: null,
          loading: false,
        });
      })
      .catch((unknown: unknown) => {
        console.error(unknown);
        setState({
          data: null,
          error: unknown as Error,
          loading: false,
        });
      });
  };

  return [state, exportLeakedCodesFn];
};
